/* eslint-disable ember/no-observers */
import { tracked } from "@glimmer/tracking";
import EmberObject, { computed, get, getProperties, set } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import Evented from "@ember/object/evented";
import { getOwner, setOwner } from "@ember/owner";
import { trackedArray } from "@ember/reactive/collections";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { camelize } from "@ember/string";
import { trustHTML } from "@ember/template";
import { isEmpty } from "@ember/utils";
import { Promise } from "rsvp";
import { ajax } from "discourse/lib/ajax";
import {
  addUniqueValueToArray,
  removeValueFromArray,
  uniqueItemsFromArray,
} from "discourse/lib/array-tools";
import {
  AUTO_GROUPS,
  INTERFACE_COLOR_MODES,
  USER_OPTION_COMPOSITION_MODES,
} from "discourse/lib/constants";
import cookie, { removeCookie } from "discourse/lib/cookie";
import deprecated from "discourse/lib/deprecated";
import { isTesting } from "discourse/lib/environment";
import { longDate } from "discourse/lib/formatter";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import getURL, { getURLWithCDN } from "discourse/lib/get-url";
import discourseLater from "discourse/lib/later";
import { NotificationLevels } from "discourse/lib/notification-levels";
import PreloadStore from "discourse/lib/preload-store";
import singleton from "discourse/lib/singleton";
import { emojiUnescape } from "discourse/lib/text";
import { autoTrackedArray } from "discourse/lib/tracked-tools";
import {
  applyBehaviorTransformer,
  applyValueTransformer,
} from "discourse/lib/transformer";
import { userPath } from "discourse/lib/url";
import { defaultHomepage, escapeExpression } from "discourse/lib/utilities";
import Badge from "discourse/models/badge";
import Bookmark from "discourse/models/bookmark";
import Category from "discourse/models/category";
import Group from "discourse/models/group";
import RestModel from "discourse/models/rest";
import Site from "discourse/models/site";
import UserAction from "discourse/models/user-action";
import UserActionStat from "discourse/models/user-action-stat";
import UserBadge from "discourse/models/user-badge";
import UserDraftsStream from "discourse/models/user-drafts-stream";
import UserPostsStream from "discourse/models/user-posts-stream";
import UserStream from "discourse/models/user-stream";
import { i18n } from "discourse-i18n";

export const SECOND_FACTOR_METHODS = {
  TOTP: 1,
  BACKUP_CODE: 2,
  SECURITY_KEY: 3,
  PASSKEY: 4,
};

export const MAX_SECOND_FACTOR_NAME_LENGTH = 300;

const TEXT_SIZE_COOKIE_NAME = "text_size";
const COOKIE_EXPIRY_DAYS = 365;

export function extendTextSizeCookie() {
  const currentValue = cookie(TEXT_SIZE_COOKIE_NAME);
  if (currentValue) {
    cookie(TEXT_SIZE_COOKIE_NAME, currentValue, {
      path: "/",
      expires: COOKIE_EXPIRY_DAYS,
    });
  }
}

const isForever = (dt) => moment().diff(dt, "years") < -100;

let userFields = [
  "allowed_pm_usernames",
  "bio_raw",
  "card_background_upload_url",
  "custom_fields",
  "date_of_birth",
  "flair_group_id",
  "ignored_usernames",
  "locale",
  "location",
  "muted_tags",
  "muted_usernames",
  "name",
  "primary_group_id",
  "profile_background_upload_url",
  "sidebar_category_ids",
  "sidebar_tag_names",
  "status",
  "title",
  "tracked_tags",
  "user_fields",
  "user_notification_schedule",
  "watched_tags",
  "watching_first_post_tags",
  "website",
];

export function addSaveableUserField(fieldName) {
  userFields.push(fieldName);
}

let userOptionFields = [
  "allow_private_messages",
  "auto_track_topics_after_msecs",
  "automatically_unpin_topics",
  "bookmark_auto_delete_preference",
  "color_scheme_id",
  "composition_mode",
  "dark_scheme_id",
  "default_calendar",
  "digest_after_minutes",
  "dynamic_favicon",
  "email_digests",
  "email_in_reply_to",
  "email_level",
  "email_messages_level",
  "email_previous_replies",
  "enable_allowed_pm_users",
  "enable_defer",
  "enable_markdown_monospace_font",
  "enable_quoting",
  "enable_smart_lists",
  "enable_upcoming_change_available_notifications",
  "external_links_in_new_tab",
  "hide_presence",
  "hide_profile",
  "homepage_id",
  "include_tl0_in_digests",
  "interface_color_mode",
  "like_notification_frequency",
  "mailing_list_mode",
  "mailing_list_mode_frequency",
  "new_topic_duration_minutes",
  "notification_level_when_replying",
  "notify_on_linked_posts",
  "push_notification_level",
  "seen_popups",
  "show_original_content",
  "sidebar_link_to_filtered_list",
  "sidebar_show_count_of_new_items",
  "skip_new_user_tips",
  "text_size",
  "theme_ids",
  "timezone",
  "title_count_mode",
  "topics_unread_when_closed",
  "watched_precedence_over_muted",
];

export function addSaveableUserOptionField(fieldName) {
  userOptionFields.push(fieldName);
}

function userOption(userOptionKey) {
  return computed(`user_option.${userOptionKey}`, {
    get(key) {
      deprecated(
        `Getting ${key} property of user object is deprecated. Use user_option object instead`,
        {
          id: "discourse.user.userOptions",
          since: "2.9.0.beta12",
        }
      );

      return this.get(`user_option.${key}`);
    },

    set(key, value) {
      deprecated(
        `Setting ${key} property of user object is deprecated. Use user_option object instead`,
        {
          id: "discourse.user.userOptions",
          since: "2.9.0.beta12",
        }
      );

      if (!this.user_option) {
        this.set("user_option", {});
      }

      return this.set(`user_option.${key}`, value);
    },
  });
}

@singleton
export default class User extends RestModel.extend(Evented) {
  static createCurrent() {
    const userJson = PreloadStore.get("currentUser");
    if (userJson) {
      userJson.isCurrent = true;

      if (userJson.primary_group_id) {
        const primaryGroup = userJson.groups.find(
          (group) => group.id === userJson.primary_group_id
        );
        if (primaryGroup) {
          userJson.primary_group_name = primaryGroup.name;
        }
      }

      if (!userJson.user_option.timezone) {
        userJson.user_option.timezone = moment.tz.guess();
        this._saveTimezone(userJson);
      }

      const store = getOwnerWithFallback(this).lookup("service:store");
      const currentUser = store.createRecord("user", userJson);
      currentUser.statusManager.trackStatus();
      return currentUser;
    }

    return null;
  }

  @service appEvents;

  @tracked do_not_disturb_until;
  @tracked status;
  @tracked dismissed_banner_key;
  @autoTrackedArray associated_accounts;
  @autoTrackedArray ignored_usernames;
  @autoTrackedArray ignored_users;
  @autoTrackedArray secondary_emails;
  @autoTrackedArray sidebar_sections;
  @autoTrackedArray unconfirmed_emails;

  @userOption("mailing_list_mode") mailing_list_mode;
  @userOption("external_links_in_new_tab") external_links_in_new_tab;
  @userOption("enable_quoting") enable_quoting;
  @userOption("enable_smart_lists") enable_smart_lists;
  @userOption("enable_markdown_monospace_font") enable_markdown_monospace_font;
  @userOption("dynamic_favicon") dynamic_favicon;
  @userOption("automatically_unpin_topics") automatically_unpin_topics;
  @userOption("likes_notifications_disabled") likes_notifications_disabled;
  @userOption("hide_profile") hide_profile;
  @userOption("hide_presence") hide_presence;
  @userOption("title_count_mode") title_count_mode;
  @userOption("enable_defer") enable_defer;
  @userOption("timezone") timezone;
  @userOption("skip_new_user_tips") skip_new_user_tips;
  @userOption("default_calendar") default_calendar;
  @userOption("bookmark_auto_delete_preference")
  bookmark_auto_delete_preference;
  @userOption("seen_popups") seen_popups;
  @userOption("should_be_redirected_to_top") should_be_redirected_to_top;
  @userOption("redirected_to_top") redirected_to_top;
  @userOption("treat_as_new_topic_start_date") treat_as_new_topic_start_date;
  @userOption("composition_mode") composition_mode;

  numGroupsToDisplay = 2;

  statusManager = new UserStatusManager(this);

  @tracked _location;

  @computed("private_messages_stats.all")
  get hasPMs() {
    return this.private_messages_stats?.all > 0;
  }

  @computed("private_messages_stats.mine")
  get hasStartedPMs() {
    return this.private_messages_stats?.mine > 0;
  }

  @computed("private_messages_stats.unread")
  get hasUnreadPMs() {
    return this.private_messages_stats?.unread > 0;
  }

  @computed("id", "username_lower")
  get adminPath() {
    return getURL(`/admin/users/${this.id}/${this.username_lower}`);
  }

  @computed("trust_level")
  get isBasic() {
    return this.trust_level === 0;
  }

  @computed("trust_level")
  get isRegular() {
    return this.trust_level === 3;
  }

  @computed("trust_level")
  get isLeader() {
    return this.trust_level === 4;
  }

  @computed("staff", "isLeader")
  get canManageTopic() {
    return this.staff || this.isLeader;
  }

  @computed("can_set_topic_timer", "canManageTopic")
  get canSetTopicTimer() {
    return this.can_set_topic_timer ?? this.canManageTopic;
  }

  @computed("sidebar_category_ids")
  get sidebarCategoryIds() {
    return this.sidebar_category_ids;
  }

  set sidebarCategoryIds(value) {
    set(this, "sidebar_category_ids", value);
  }

  @dependentKeyCompat
  get sidebarSections() {
    return this.sidebar_sections;
  }

  set sidebarSections(value) {
    this.sidebar_sections = value;
  }

  @dependentKeyCompat
  get location() {
    return applyValueTransformer("user-location", this._location, {
      user: this,
    });
  }

  set location(value) {
    this._location = value;
  }

  @computed("sidebarTags.@each.name")
  get sidebarTagNames() {
    return this.sidebarTags?.map?.((item) => item.name) ?? [];
  }

  @computed("groups.@each.has_messages")
  get groupsWithMessages() {
    return this.groups?.filter?.((item) => item.has_messages === true) ?? [];
  }

  @computed("can_pick_theme_with_custom_homepage")
  get canPickThemeWithCustomHomepage() {
    return this.can_pick_theme_with_custom_homepage;
  }

  set canPickThemeWithCustomHomepage(value) {
    set(this, "can_pick_theme_with_custom_homepage", value);
  }

  @computed("can_edit_tags")
  get canEditTags() {
    return this.can_edit_tags;
  }

  set canEditTags(value) {
    set(this, "can_edit_tags", value);
  }

  @computed("can_change_post_owner")
  get canChangePostOwner() {
    return this.can_change_post_owner;
  }

  set canChangePostOwner(value) {
    set(this, "can_change_post_owner", value);
  }

  @computed("user_option.composition_mode")
  get useRichEditor() {
    return (
      this.user_option?.composition_mode === USER_OPTION_COMPOSITION_MODES.rich
    );
  }

  @computed("can_be_deleted", "post_count")
  get canBeDeleted() {
    const maxPostCount = this.siteSettings.delete_all_posts_max;
    return this.can_be_deleted && this.post_count <= maxPostCount;
  }

  @computed()
  get stream() {
    return UserStream.create({ user: this });
  }

  @computed()
  get bookmarks() {
    return Bookmark.create({ user: this });
  }

  @computed()
  get postsStream() {
    return UserPostsStream.create({ user: this });
  }

  @computed()
  get userDraftsStream() {
    return UserDraftsStream.create({ user: this });
  }

  @computed("admin", "moderator")
  get staff() {
    return this.admin || this.moderator;
  }

  // prevents staff property to be overridden
  set staff(value) {}

  @computed("has_unseen_features")
  get hasUnseenFeatures() {
    return this.staff && this.get("has_unseen_features");
  }

  @computed("has_new_upcoming_changes")
  get hasNewUpcomingChanges() {
    return this.staff && this.get("has_new_upcoming_changes");
  }

  destroySession(pushSubscription) {
    const data = {};
    if (pushSubscription) {
      data.push_subscription = pushSubscription;
    }
    return ajax(`/session/${this.username}`, { type: "DELETE", data });
  }

  @computed("username_lower")
  get searchContext() {
    return {
      type: "user",
      id: this.username_lower,
      /** @type User */
      user: this,
    };
  }

  @computed("username", "name")
  get displayName() {
    if (this.siteSettings.enable_names && !isEmpty(this.name)) {
      return this.name;
    }
    return this.username;
  }

  @computed("profile_background_upload_url")
  get profileBackgroundUrl() {
    if (
      isEmpty(this.profile_background_upload_url) ||
      !this.siteSettings.allow_profile_backgrounds
    ) {
      return trustHTML("");
    }
    return trustHTML(
      "background-image: url(" +
        getURLWithCDN(this.profile_background_upload_url) +
        ")"
    );
  }

  @computed()
  get path() {
    // no need to observe, requires a hard refresh to update
    return applyValueTransformer("user-path", userPath(this.username_lower), {
      user: this,
    });
  }

  @computed()
  get userApiKeys() {
    const keys = this.user_api_keys;
    if (keys) {
      return keys.map((raw) => {
        let obj = EmberObject.create(raw);

        obj.revoke = () => {
          this.revokeApiKey(obj);
        };

        obj.undoRevoke = () => {
          this.undoRevokeApiKey(obj);
        };

        return obj;
      });
    }
  }

  revokeApiKey(key) {
    return ajax("/user-api-key/revoke", {
      type: "POST",
      data: { id: key.get("id") },
    }).then(() => {
      key.set("revoked", true);
    });
  }

  undoRevokeApiKey(key) {
    return ajax("/user-api-key/undo-revoke", {
      type: "POST",
      data: { id: key.get("id") },
    }).then(() => {
      key.set("revoked", false);
    });
  }

  pmPath(topic) {
    const username = this.username_lower;
    const details = topic.details;
    const allowedUsers = details?.allowed_users;
    const groups = details?.allowed_groups;

    // directly targeted so go to inbox
    if (!groups || allowedUsers?.find((user) => user.id === this.id)) {
      return userPath(`${username}/messages`);
    } else if (groups) {
      const firstAllowedGroup = groups.find((allowedGroup) =>
        this.groups.some((userGroup) => userGroup.id === allowedGroup.id)
      );

      if (firstAllowedGroup) {
        return userPath(`${username}/messages/group/${firstAllowedGroup.name}`);
      }
    }
  }

  @computed()
  get mutedTopicsPath() {
    return defaultHomepage() === "latest"
      ? getURL("/?state=muted")
      : getURL("/latest?state=muted");
  }

  @computed()
  get watchingTopicsPath() {
    return defaultHomepage() === "latest"
      ? getURL("/?state=watching")
      : getURL("/latest?state=watching");
  }

  @computed()
  get trackingTopicsPath() {
    return defaultHomepage() === "latest"
      ? getURL("/?state=tracking")
      : getURL("/latest?state=tracking");
  }

  @computed("username")
  get username_lower() {
    return this.username.toLowerCase();
  }

  @computed("trust_level")
  get trustLevel() {
    return Site.currentProp("trustLevels").find(
      (l) => l.id === parseInt(this.trust_level, 10)
    );
  }

  @computed("previous_visit_at")
  get previousVisitAt() {
    return new Date(this.previous_visit_at);
  }

  @computed("suspended_till")
  get suspended() {
    return this.suspended_till && moment(this.suspended_till).isAfter();
  }

  @computed("suspended_till")
  get suspendedForever() {
    return isForever(this.suspended_till);
  }

  @computed("silenced_till")
  get silenced() {
    return this.silenced_till && moment(this.silenced_till).isAfter();
  }

  @computed("silenced_till")
  get silencedForever() {
    return isForever(this.silenced_till);
  }

  @computed("suspended_till")
  get suspendedTillDate() {
    return longDate(this.suspended_till);
  }

  @computed("silenced_till")
  get silencedTillDate() {
    return longDate(this.silenced_till);
  }

  @computed("sidebar_tags.[]")
  get sidebarTags() {
    if (!this.sidebar_tags || this.sidebar_tags?.length === 0) {
      return [];
    }

    return this.sidebar_tags?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  changeUsername(new_username) {
    return ajax(userPath(`${this.username_lower}/preferences/username`), {
      type: "PUT",
      data: { new_username },
    });
  }

  addEmail(email) {
    return ajax(userPath(`${this.username_lower}/preferences/email`), {
      type: "POST",
      data: { email },
    }).then(() => {
      if (!this.unconfirmed_emails) {
        this.set("unconfirmed_emails", []);
      }

      if (!this.unconfirmed_emails.includes(email)) {
        this.unconfirmed_emails.push(email);
      }
    });
  }

  changeEmail(email) {
    return ajax(userPath(`${this.username_lower}/preferences/email`), {
      type: "PUT",
      data: { email },
    }).then(() => {
      if (!this.unconfirmed_emails) {
        this.set("unconfirmed_emails", []);
      }

      this.unconfirmed_emails.push(email);
    });
  }

  save(fields) {
    const data = this.getProperties(
      userFields.filter((uf) => !fields || fields.includes(uf))
    );

    const filteredUserOptionFields = fields
      ? userOptionFields.filter((uo) => fields.includes(uo))
      : userOptionFields;

    filteredUserOptionFields.forEach((s) => {
      data[s] = this.get(`user_option.${s}`);
    });

    const updatedState = {};

    ["muted", "regular", "watched", "tracked", "watched_first_post"].forEach(
      (categoryNotificationLevel) => {
        if (
          fields === undefined ||
          fields.includes(`${categoryNotificationLevel}_category_ids`)
        ) {
          const categories = this.get(
            `${camelize(categoryNotificationLevel)}Categories`
          );

          if (categories) {
            const ids = categories.map((c) => c.get("id"));
            updatedState[`${categoryNotificationLevel}_category_ids`] = ids;
            // HACK: Empty arrays are not sent in the request, we use [-1],
            // an invalid category ID, that will be ignored by the server.
            data[`${categoryNotificationLevel}_category_ids`] =
              ids.length === 0 ? [-1] : ids;
          }
        }
      }
    );

    [
      "muted_tags",
      "tracked_tags",
      "watched_tags",
      "watching_first_post_tags",
    ].forEach((prop) => {
      if (fields === undefined || fields.includes(prop)) {
        const tags = this.get(prop);
        if (tags) {
          const names = tags.map((t) => (typeof t === "object" ? t.name : t));
          data[prop] = names.join(",");
        } else {
          data[prop] = "";
        }
      }
    });

    ["sidebar_category_ids", "sidebar_tag_names"].forEach((prop) => {
      if (data[prop]?.length === 0) {
        data[prop] = null;
      }
    });

    // TODO: We can remove this when migrated fully to rest model.
    this.set("isSaving", true);
    return ajax(userPath(`${this.username_lower}.json`), {
      data,
      type: "PUT",
    })
      .then((result) => {
        this.setProperties(updatedState);
        this.setProperties(getProperties(result.user, "bio_excerpt"));
        return result;
      })
      .finally(() => {
        this.set("isSaving", false);
      });
  }

  setPrimaryEmail(email) {
    return ajax(userPath(`${this.username}/preferences/primary-email.json`), {
      type: "PUT",
      data: { email },
    }).then(() => {
      removeValueFromArray(this.secondary_emails, email);
      this.secondary_emails.push(this.email);
      this.set("email", email);
    });
  }

  async destroyEmail(email) {
    await ajax(userPath(`${this.username}/preferences/email.json`), {
      type: "DELETE",
      data: { email },
    });

    removeValueFromArray(
      this.unconfirmed_emails.includes(email)
        ? this.unconfirmed_emails
        : this.secondary_emails,
      email
    );
  }

  changePassword() {
    return ajax("/session/forgot_password.json", {
      data: { login: this.email || this.username },
      type: "POST",
    });
  }

  async removePassword() {
    return ajax(userPath(`${this.username}/remove-password`), {
      type: "PUT",
    });
  }

  loadSecondFactorCodes() {
    return ajax("/u/second_factors.json", {
      type: "POST",
    });
  }

  requestSecurityKeyChallenge() {
    return ajax("/u/create_second_factor_security_key.json", {
      type: "POST",
    });
  }

  registerSecurityKey(credential) {
    return ajax("/u/register_second_factor_security_key.json", {
      data: credential,
      type: "POST",
    });
  }

  trustedSession() {
    return ajax("/u/trusted-session.json");
  }

  createPasskey() {
    return ajax("/u/create_passkey.json", {
      type: "POST",
    });
  }

  registerPasskey(credential) {
    return ajax("/u/register_passkey.json", {
      data: credential,
      type: "POST",
    });
  }

  deletePasskey(id) {
    return ajax(`/u/delete_passkey/${id}`, {
      type: "DELETE",
    });
  }

  createSecondFactorTotp() {
    return ajax("/u/create_second_factor_totp.json", {
      type: "POST",
    });
  }

  enableSecondFactorTotp(authToken, name) {
    return ajax("/u/enable_second_factor_totp.json", {
      data: {
        second_factor_token: authToken,
        name,
      },
      type: "POST",
    });
  }

  disableAllSecondFactors() {
    return ajax("/u/disable_second_factor.json", {
      type: "PUT",
    });
  }

  updateSecondFactor(id, name, disable, targetMethod) {
    return ajax("/u/second_factor.json", {
      data: {
        second_factor_target: targetMethod,
        name,
        disable,
        id,
      },
      type: "PUT",
    });
  }

  updateSecurityKey(id, name, disable) {
    return ajax("/u/security_key.json", {
      data: {
        name,
        disable,
        id,
      },
      type: "PUT",
    });
  }

  toggleSecondFactor(authToken, authMethod, targetMethod, enable) {
    return ajax("/u/second_factor.json", {
      data: {
        second_factor_token: authToken,
        second_factor_method: authMethod,
        second_factor_target: targetMethod,
        enable,
      },
      type: "PUT",
    });
  }

  generateSecondFactorCodes() {
    return ajax("/u/second_factors_backup.json", {
      type: "PUT",
    });
  }

  revokeAssociatedAccount(providerName) {
    return ajax(userPath(`${this.username}/preferences/revoke-account`), {
      data: { provider_name: providerName },
      type: "POST",
    });
  }

  async loadUserAction(id) {
    const result = await ajax(`/user_actions/${id}.json`);

    if (!result?.user_action) {
      return;
    }

    const ua = result.user_action;

    if ((this.get("stream.filter") || ua.action_type) !== ua.action_type) {
      return;
    }

    if (!this.get("stream.filter") && !this.inAllStream(ua)) {
      return;
    }

    ua.title = emojiUnescape(escapeExpression(ua.title));
    const action = UserAction.collapseStream([UserAction.create(ua)]);
    this.stream.itemsLoaded += 1;
    this.stream.content.unshift(action[0]);
  }

  inAllStream(ua) {
    return (
      ua.action_type === UserAction.TYPES.posts ||
      ua.action_type === UserAction.TYPES.topics
    );
  }

  @computed("groups.[]")
  get filteredGroups() {
    const groups = this.groups || [];

    return groups.filter((group) => {
      return !group.automatic || group.id === AUTO_GROUPS.moderators.id;
    });
  }

  @computed("filteredGroups", "numGroupsToDisplay")
  get displayGroups() {
    const groups = this.filteredGroups.slice(0, this.numGroupsToDisplay);
    return groups.length === 0 ? null : groups;
  }

  // The user's stat count, excluding PMs.
  @computed("statsExcludingPms.@each.count")
  get statsCountNonPM() {
    if (isEmpty(this.statsExcludingPms)) {
      return 0;
    }
    let count = 0;
    this.statsExcludingPms.forEach((val) => {
      if (this.inAllStream(val)) {
        count += val.count;
      }
    });
    return count;
  }

  // The user's stats, excluding PMs.
  @computed("stats.@each.isPM")
  get statsExcludingPms() {
    if (isEmpty(this.stats)) {
      return [];
    }
    return this.stats.filter((stat) => !stat.isPM);
  }

  findDetails(options) {
    const user = this;

    return PreloadStore.getAndRemove(`user_${user.get("username")}`, () => {
      if (options && options.existingRequest) {
        // Existing ajax request has been passed, use it
        return options.existingRequest;
      }

      const useCardRoute = options && options.forCard;
      if (options) {
        delete options.forCard;
      }

      const path = useCardRoute
        ? `${user.get("username")}/card.json`
        : `${user.get("username")}.json`;

      return ajax(userPath(path), { data: options });
    }).then((json) => {
      if (!isEmpty(json.user.stats)) {
        json.user.stats = User.groupStats(
          json.user.stats.map((s) => {
            if (s.count) {
              s.count = parseInt(s.count, 10);
            }
            return UserActionStat.create(s);
          })
        );
      }

      if (!isEmpty(json.user.groups) && !isEmpty(json.user.group_users)) {
        const groups = [];

        for (let i = 0; i < json.user.groups.length; i++) {
          const group = Group.create(json.user.groups[i]);
          group.group_user = json.user.group_users[i];
          groups.push(group);
        }

        json.user.groups = groups;
      }

      if (json.user.invited_by) {
        json.user.invited_by = User.create(json.user.invited_by);
      }

      if (!isEmpty(json.user.featured_user_badge_ids)) {
        const userBadgesMap = {};
        UserBadge.createFromJson(json).forEach((userBadge) => {
          userBadgesMap[userBadge.get("id")] = userBadge;
        });
        json.user.featured_user_badges = json.user.featured_user_badge_ids.map(
          (id) => userBadgesMap[id]
        );
      }

      if (json.user.card_badge) {
        json.user.card_badge = Badge.create(json.user.card_badge);
      }

      const timezone = json.user.timezone;
      delete json.user.timezone;

      user.setProperties(json.user);

      if (timezone) {
        user.user_option ||= {};
        user.user_option.timezone = timezone;
      }

      return user;
    });
  }

  findStaffInfo() {
    if (!User.currentProp("staff")) {
      return Promise.resolve(null);
    }
    return ajax(userPath(`${this.username_lower}/staff-info.json`)).then(
      (info) => {
        this.setProperties(info);
      }
    );
  }

  pickAvatar(upload_id, type) {
    return ajax(userPath(`${this.username_lower}/preferences/avatar/pick`), {
      type: "PUT",
      data: { upload_id, type },
    });
  }

  selectAvatar(avatarUrl) {
    return ajax(userPath(`${this.username_lower}/preferences/avatar/select`), {
      type: "PUT",
      data: { url: avatarUrl },
    });
  }

  isAllowedToUploadAFile(type) {
    const settingName = type === "image" ? "embedded_media" : "attachments";

    return (
      this.staff ||
      this.trust_level > 0 ||
      this.siteSettings[`newuser_max_${settingName}`] > 0
    );
  }

  createInvite(email, group_ids, custom_message) {
    return ajax("/invites", {
      type: "POST",
      data: { email, group_ids, custom_message },
    });
  }

  generateInviteLink(email, group_ids, topic_id) {
    return ajax("/invites", {
      type: "POST",
      data: { email, skip_email: true, group_ids, topic_id },
    });
  }

  @dependentKeyCompat
  get mutedCategories() {
    if (
      this.site.lazy_load_categories &&
      this.muted_category_ids &&
      !Category.hasAsyncFoundAll(this.muted_category_ids)
    ) {
      Category.asyncFindByIds(this.muted_category_ids).then(() =>
        this.notifyPropertyChange("muted_category_ids")
      );
    }

    return Category.findByIds(this.get("muted_category_ids"));
  }

  set mutedCategories(categories) {
    this.set(
      "muted_category_ids",
      categories.map((c) => c.id)
    );
  }

  @dependentKeyCompat
  get regularCategories() {
    if (
      this.site.lazy_load_categories &&
      this.regular_category_ids &&
      !Category.hasAsyncFoundAll(this.regular_category_ids)
    ) {
      Category.asyncFindByIds(this.regular_category_ids).then(() =>
        this.notifyPropertyChange("regular_category_ids")
      );
    }

    return Category.findByIds(this.get("regular_category_ids"));
  }

  set regularCategories(categories) {
    this.set(
      "regular_category_ids",
      categories.map((c) => c.id)
    );
  }

  @dependentKeyCompat
  get trackedCategories() {
    if (
      this.site.lazy_load_categories &&
      this.tracked_category_ids &&
      !Category.hasAsyncFoundAll(this.tracked_category_ids)
    ) {
      Category.asyncFindByIds(this.tracked_category_ids).then(() =>
        this.notifyPropertyChange("tracked_category_ids")
      );
    }

    return Category.findByIds(this.get("tracked_category_ids"));
  }

  set trackedCategories(categories) {
    this.set(
      "tracked_category_ids",
      categories.map((c) => c.id)
    );
  }

  @dependentKeyCompat
  get watchedCategories() {
    if (
      this.site.lazy_load_categories &&
      this.watched_category_ids &&
      !Category.hasAsyncFoundAll(this.watched_category_ids)
    ) {
      Category.asyncFindByIds(this.watched_category_ids).then(() =>
        this.notifyPropertyChange("watched_category_ids")
      );
    }

    return Category.findByIds(this.get("watched_category_ids"));
  }

  set watchedCategories(categories) {
    this.set(
      "watched_category_ids",
      categories.map((c) => c.id)
    );
  }

  @dependentKeyCompat
  get watchedFirstPostCategories() {
    if (
      this.site.lazy_load_categories &&
      this.watched_first_post_category_ids &&
      !Category.hasAsyncFoundAll(this.watched_first_post_category_ids)
    ) {
      Category.asyncFindByIds(this.watched_first_post_category_ids).then(() =>
        this.notifyPropertyChange("watched_first_post_category_ids")
      );
    }

    return Category.findByIds(this.get("watched_first_post_category_ids"));
  }

  set watchedFirstPostCategories(categories) {
    this.set(
      "watched_first_post_category_ids",
      categories.map((c) => c.id)
    );
  }

  @computed("can_delete_account")
  get canDeleteAccount() {
    return (
      !this.siteSettings.enable_discourse_connect && this.can_delete_account
    );
  }

  @dependentKeyCompat
  get sidebarLinkToFilteredList() {
    return this.get("user_option.sidebar_link_to_filtered_list");
  }

  @dependentKeyCompat
  get sidebarShowCountOfNewItems() {
    return this.get("user_option.sidebar_show_count_of_new_items");
  }

  delete() {
    if (this.can_delete_account) {
      return ajax(userPath(this.username + ".json"), {
        type: "DELETE",
        data: { context: window.location.pathname },
      });
    } else {
      return Promise.reject(i18n("user.delete_yourself_not_allowed"));
    }
  }

  updateNotificationLevel({ level, expiringAt = null, actingUser = null }) {
    actingUser ||= User.current();

    return ajax(`${userPath(this.username)}/notification_level.json`, {
      type: "PUT",
      data: {
        notification_level: level,
        expiring_at: expiringAt,
        acting_user_id: actingUser.id,
      },
    }).then(() => {
      if (!actingUser.ignored_users) {
        actingUser.ignored_users = [];
      }
      if (level === "normal" || level === "mute") {
        removeValueFromArray(actingUser.ignored_users, this.username);
      } else if (level === "ignore") {
        addUniqueValueToArray(actingUser.ignored_users, this.username);
      }
    });
  }

  dismissBanner(bannerKey) {
    this.set("dismissed_banner_key", bannerKey);
    ajax(userPath(this.username + ".json"), {
      type: "PUT",
      data: { dismissed_banner_key: bannerKey },
    });
  }

  checkEmail() {
    return ajax(userPath(`${this.username_lower}/emails.json`), {
      data: { context: window.location.pathname },
    }).then((result) => {
      if (result) {
        this.setProperties({
          email: result.email,
          secondary_emails: result.secondary_emails,
          unconfirmed_emails: result.unconfirmed_emails,
          associated_accounts: result.associated_accounts,
        });
      }
    });
  }

  summary() {
    const store = getOwnerWithFallback(this).lookup("service:store");

    return ajax(userPath(`${this.username_lower}/summary.json`)).then(
      (json) => {
        const summary = json.user_summary;
        const topicMap = {};
        const badgeMap = {};

        json.topics.forEach(
          (t) => (topicMap[t.id] = store.createRecord("topic", t))
        );
        Badge.createFromJson(json).forEach((b) => (badgeMap[b.id] = b));

        summary.topics = summary.topic_ids.map((id) => topicMap[id]);

        summary.replies.forEach((r) => {
          r.topic = topicMap[r.topic_id];
          r.url = r.topic.urlForPostNumber(r.post_number);
          r.createdAt = new Date(r.created_at);
        });

        summary.links.forEach((l) => {
          l.topic = topicMap[l.topic_id];
          l.post_url = l.topic.urlForPostNumber(l.post_number);
        });

        if (summary.badges) {
          summary.badges = summary.badges.map((ub) => {
            const badge = badgeMap[ub.badge_id];
            badge.count = ub.count;
            return badge;
          });
        }

        if (summary.top_categories) {
          summary.top_categories.forEach((c) => {
            if (c.parent_category_id) {
              c.parentCategory = Category.findById(c.parent_category_id);
            }
          });
        }

        return summary;
      }
    );
  }

  canManageGroup(group) {
    return group.get("can_admin_group") || group.get("is_group_owner");
  }

  @computed("groups.@each.title", "badges.[]")
  get availableTitles() {
    const titles = [];

    (this.groups || []).forEach((group) => {
      if (get(group, "title")) {
        titles.push(get(group, "title"));
      }
    });

    (this.badges || []).forEach((badge) => {
      if (get(badge, "allow_title")) {
        titles.push(get(badge, "name"));
      }
    });

    return uniqueItemsFromArray(titles)
      .sort()
      .map((title) => {
        return {
          name: escapeExpression(title),
          id: title,
        };
      });
  }

  @computed("groups.[]")
  get availableFlairs() {
    const flairs = [];

    if (this.groups) {
      this.groups.forEach((group) => {
        if (group.flair_url) {
          flairs.push({
            id: group.id,
            name: group.name,
            url: group.flair_url,
            bgColor: group.flair_bg_color,
            color: group.flair_color,
          });
        }
      });
    }

    return flairs;
  }

  @computed("user_option.text_size_seq", "user_option.text_size")
  get currentTextSize() {
    if (cookie(TEXT_SIZE_COOKIE_NAME)) {
      const [cookieSize, cookieSeq] = cookie(TEXT_SIZE_COOKIE_NAME).split("|");
      if (cookieSeq >= this.user_option?.text_size_seq) {
        return cookieSize;
      }
    }
    return this.user_option?.text_size;
  }

  updateTextSizeCookie(newSize) {
    if (newSize) {
      const seq = this.get("user_option.text_size_seq");
      cookie(TEXT_SIZE_COOKIE_NAME, `${newSize}|${seq}`, {
        path: "/",
        expires: COOKIE_EXPIRY_DAYS,
      });
    } else {
      removeCookie(TEXT_SIZE_COOKIE_NAME, { path: "/" });
    }
  }

  @computed("second_factor_enabled", "staff")
  get enforcedSecondFactor() {
    const enforce = this.siteSettings.enforce_second_factor;
    return (
      !this.second_factor_enabled &&
      (enforce === "all" || (enforce === "staff" && this.staff))
    );
  }

  resolvedTimezone() {
    deprecated(
      "user.resolvedTimezone() has been deprecated. Use user.user_option.timezone instead",
      {
        id: "discourse.user.resolved-timezone",
        since: "2.9.0.beta12",
      }
    );

    return this.user_option.timezone;
  }

  calculateMutedIds(notificationLevel, id, type) {
    const muted_ids = this.get(type);
    if (notificationLevel === NotificationLevels.MUTED) {
      return uniqueItemsFromArray(muted_ids.concat(id));
    } else {
      return muted_ids.filter((existing_id) => existing_id !== id);
    }
  }

  setPrimaryGroup(primaryGroupId) {
    return ajax(`/admin/users/${this.id}/primary_group`, {
      type: "PUT",
      data: { primary_group_id: primaryGroupId },
    });
  }

  enterDoNotDisturbFor(duration) {
    return ajax({
      url: "/do-not-disturb.json",
      type: "POST",
      data: { duration },
    }).then((response) => {
      return this.updateDoNotDisturbStatus(response.ends_at);
    });
  }

  leaveDoNotDisturb() {
    return ajax({
      url: "/do-not-disturb.json",
      type: "DELETE",
    }).then(() => {
      this.updateDoNotDisturbStatus(null);
    });
  }

  updateDoNotDisturbStatus(ends_at) {
    this.set("do_not_disturb_until", ends_at);
    this.appEvents.trigger("do-not-disturb:changed", this.do_not_disturb_until);
    getOwner(this).lookup("service:notifications")._checkDoNotDisturb();
  }

  updateDraftProperties(properties) {
    this.setProperties(properties);
    this.appEvents.trigger("user-drafts:changed");
  }

  updateReviewableCount(count) {
    this.set("reviewable_count", count);
    this.appEvents.trigger("user-reviewable-count:changed", count);
  }

  isInDoNotDisturb() {
    if (this !== getOwner(this).lookup("service:current-user")) {
      throw "isInDoNotDisturb is only supported for currentUser";
    }

    return getOwner(this).lookup("service:notifications").isInDoNotDisturb;
  }

  @computed("tracked_tags.[]", "watched_tags.[]", "watching_first_post_tags.[]")
  get trackedTags() {
    return [
      ...(this.tracked_tags || []),
      ...(this.watched_tags || []),
      ...(this.watching_first_post_tags || []),
    ];
  }

  get prefersLightColor() {
    return (
      this.user_option?.interface_color_mode === INTERFACE_COLOR_MODES.LIGHT
    );
  }

  get prefersDarkColor() {
    return (
      this.user_option?.interface_color_mode === INTERFACE_COLOR_MODES.DARK
    );
  }

  get prefersAutoColor() {
    return (
      this.user_option?.interface_color_mode === INTERFACE_COLOR_MODES.AUTODARK
    );
  }
}

User.reopenClass({
  // Find a `User` for a given username.
  findByUsername(username, options) {
    const user = User.create({ username });
    return user.findDetails(options);
  },

  checkUsername(username, email, for_user_id) {
    return ajax(userPath("check_username"), {
      data: { username, email, for_user_id },
    });
  },

  checkEmail(email) {
    return ajax(userPath("check_email"), { data: { email } });
  },

  loadRecentSearches() {
    return ajax(`/u/recent-searches`);
  },

  resetRecentSearches() {
    return ajax(`/u/recent-searches`, { type: "DELETE" });
  },

  groupStats(stats) {
    const responses = UserActionStat.create({
      count: 0,
      action_type: UserAction.TYPES.replies,
    });

    stats
      .filter((stat) => stat.isResponse)
      .forEach((stat) => {
        responses.set("count", responses.get("count") + stat.get("count"));
      });

    const result = trackedArray();
    result.push(...stats.filter((stat) => !stat.isResponse));

    let insertAt = 0;
    result.forEach((item, index) => {
      if (
        item.action_type === UserAction.TYPES.topics ||
        item.action_type === UserAction.TYPES.posts
      ) {
        insertAt = index + 1;
      }
    });
    if (responses.count > 0) {
      result.splice(insertAt, 0, responses);
    }
    return result;
  },

  async createAccount(attrs) {
    let data = {
      name: attrs.accountName,
      email: attrs.accountEmail,
      password: attrs.accountPassword,
      username: attrs.accountUsername,
      password_confirmation: attrs.accountPasswordConfirm,
      challenge: attrs.accountChallenge,
      user_fields: attrs.userFields,
      timezone: moment.tz.guess(),
    };

    if (attrs.inviteCode) {
      data.invite_code = attrs.inviteCode;
    }

    return applyBehaviorTransformer(
      "create-account",
      () =>
        ajax(userPath(), {
          data,
          type: "POST",
        }),
      { data }
    );
  },

  _saveTimezone(user) {
    ajax(userPath(user.username + ".json"), {
      type: "PUT",
      dataType: "json",
      data: { timezone: user.user_option.timezone },
    });
  },

  create(args) {
    args = args || {};
    this.deleteStatusTrackingFields(args);

    return this._super(args);
  },

  deleteStatusTrackingFields(args) {
    // every user instance has to have it's own tracking fields
    // when creating a new user model
    // its _subscribersCount and _clearStatusTimerId fields
    // should be equal to 0 and null
    // here we makes sure that even if these fields
    // will be passed in args they won't be set anyway
    //
    // this is something that could be implemented by making these fields private,
    // but EmberObject doesn't support private fields
    if (args.hasOwnProperty("_subscribersCount")) {
      delete args._subscribersCount;
    }
    if (args.hasOwnProperty("_clearStatusTimerId")) {
      delete args._clearStatusTimerId;
    }
  },
});

// user status tracking
class UserStatusManager {
  @service appEvents;

  user;
  _subscribersCount = 0;
  _clearStatusTimerId = null;

  constructor(user) {
    this.user = user;
    setOwner(this, getOwner(user));
  }

  // always call stopTrackingStatus() when done with a user
  trackStatus() {
    if (!this.user.id && !isTesting()) {
      // eslint-disable-next-line no-console
      console.warn(
        "It's impossible to track user status on a user model that doesn't have id. This user model won't be receiving live user status updates."
      );
    }

    if (this._subscribersCount === 0) {
      this.user.addObserver("status", this, "_statusChanged");

      this.appEvents.on("user-status:changed", this, this._updateStatus);

      if (this.user.status?.ends_at) {
        this._scheduleStatusClearing(this.user.status.ends_at);
      }
    }

    this._subscribersCount++;
  }

  stopTrackingStatus() {
    if (this._subscribersCount === 0) {
      return;
    }

    if (this._subscribersCount === 1) {
      // the last subscriber is unsubscribing
      this.user.removeObserver("status", this, "_statusChanged");
      this.appEvents.off("user-status:changed", this, this._updateStatus);
      this._unscheduleStatusClearing();
    }

    this._subscribersCount--;
  }

  isTrackingStatus() {
    return this._subscribersCount > 0;
  }

  _statusChanged() {
    this.user.trigger("status-changed", this.user);

    const status = this.user.status;
    if (status && status.ends_at) {
      this._scheduleStatusClearing(status.ends_at);
    } else {
      this._unscheduleStatusClearing();
    }
  }

  _scheduleStatusClearing(endsAt) {
    if (isTesting()) {
      return;
    }

    if (this._clearStatusTimerId) {
      this._unscheduleStatusClearing();
    }

    const utcNow = moment.utc();
    const remaining = moment.utc(endsAt).diff(utcNow, "milliseconds");
    this._clearStatusTimerId = discourseLater(
      this,
      "_autoClearStatus",
      remaining
    );
  }

  _unscheduleStatusClearing() {
    cancel(this._clearStatusTimerId);
    this._clearStatusTimerId = null;
  }

  _autoClearStatus() {
    this.user.status = null;
  }

  _updateStatus(statuses) {
    if (statuses.hasOwnProperty(this.user.id)) {
      this.user.status = statuses[this.user.id];
    }
  }
}

if (typeof Discourse !== "undefined") {
  let warned = false;
  // eslint-disable-next-line no-undef
  Object.defineProperty(Discourse, "User", {
    get() {
      if (!warned) {
        deprecated("Import the User class instead of using Discourse.User", {
          since: "2.4.0",
          id: "discourse.global.user",
        });
        warned = true;
      }
      return User;
    },
  });
}
